import React from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type MedcallAdministrationNotesEditorProps = {
  canEdit: boolean;
  disabled?: boolean;
  initialNotes?: string | null;
  onSave: (administrationNotes: string) => Promise<boolean>;
  preserveLegacySemantics?: boolean;
  timelineId: number;
};

export const MedcallAdministrationNotesEditor = ({
  canEdit,
  disabled = false,
  initialNotes,
  onSave,
  preserveLegacySemantics = false,
  timelineId,
}: MedcallAdministrationNotesEditorProps) => {
  const normalizedInitialNotes = initialNotes == null ? '' : `${initialNotes}`;
  const [administrationNotes, setAdministrationNotes] = React.useState(normalizedInitialNotes);
  const [draft, setDraft] = React.useState(normalizedInitialNotes);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const draftRef = React.useRef(normalizedInitialNotes);

  const handleSave = async () => {
    if (savingRef.current) return;
    const submittedNotes = draft;
    savingRef.current = true;
    if (!preserveLegacySemantics) {
      setIsSaving(true);
    }
    try {
      const saved = await onSave(submittedNotes);
      if (!saved) return;

      setAdministrationNotes(submittedNotes);
      if (draftRef.current === submittedNotes) setIsEditing(false);
    } finally {
      savingRef.current = false;
      if (!preserveLegacySemantics) setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          aria-label="Injection site location"
          className="h-8 w-[200px]"
          disabled={disabled || (!preserveLegacySemantics && isSaving)}
          id={`medcall-timeline-${timelineId}-administration-notes-input`}
          onChange={(event) => {
            draftRef.current = event.target.value;
            setDraft(event.target.value);
          }}
          type="text"
          value={draft}
        />
        <Button
          disabled={disabled || (!preserveLegacySemantics && isSaving)}
          id={`medcall-timeline-${timelineId}-administration-notes-save`}
          onClick={() => void handleSave()}
          size="sm"
          type="button"
        >
          Save
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span>{administrationNotes}</span>
      {canEdit ? (
        <Button
          aria-label="Edit injection site location"
          disabled={disabled}
          id={`medcall-timeline-${timelineId}-administration-notes-edit`}
          onClick={() => setIsEditing(true)}
          className="h-8 w-8 p-0"
          size="sm"
          type="button"
          variant="outline"
        >
          <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
};
